/**
 * 登記出貨(REQ-20260815008)。
 *
 * 訪談建檔 → 新增應收 → 商品完成後才出貨,而商品常常分批完成。
 * 互動沿用「登錄收款」(ReceiptModal):勾要出的品項 → 每筆預帶入未出貨數量
 * (要分批就自己改小)→ 填出貨日期 → 送出。同一張單可以來幾次就登記幾次,
 * 每次都留一筆紀錄,下方「歷次出貨紀錄」看得到。
 *
 * 數量只是「這次要出多少」的意圖,後端會拿當下的未出貨數量重算與驗證
 * (超賣一律 400,不會靜默夾成上限);這裡的檢查純粹是不要讓使用者按下去才發現。
 *
 * REQ-20260815010 加了選填的「本次收款」:送貨常常當場收錢,填了就在同一次送出
 * 一併登記收款(固定沖這張單)。**兩種錯是分開擋、分開顯示的** ——
 * 出貨數量超賣是一種錯,收款超過未結餘額是另一種錯,訊息不混在一起。
 *
 * REQ-20260817007 加了選填的「寄送資訊」:貨運公司(必須是已建檔的廠商,用既有的
 * 廠商搜尋開窗挑)、寄送單號、寄送金額。
 *
 * REQ-20260908002 把那一區整個換成 **「寄送方式」四選一(必填)**:
 * 客戶自取 / 我方自送 / 委外貨運 / 其他。**只有「委外貨運」會展開**貨運公司、
 * 託運單號、運費金額(未稅)、付款方式四格;「其他」展開一格說明。
 *
 * ⚠️ 送出後**不再自動開運費進貨單、不再自動沖帳**(推翻 REQ-20260829003)——
 * 只留下一筆「運費明細」,月底到「運費歸單」畫面彙整成一張進貨單、核對過廠商的
 * 月結對帳單才入帳。理由是同一家貨運行一個月送 40 趟就有 40 張進貨單,
 * 而對方只給一張對帳單,對不起來。
 * 「☑ 運費已付款 + 現金 / 支票」整組移除,由付款方式三選一取代。
 *
 * REQ-20260825003 讓「歷次出貨紀錄」的每一列可以**改出貨日期**:日期 KEY 錯是
 * 事後才會發現的事,而原本那份清單純顯示,使用者連「要改哪一筆」都指不出來。
 * 只有日期一格可改(數量 / 品項要改仍然是作廢重開),而且是行內編輯不另開彈窗。
 * 能不能改由後端決定 —— 已沖帳時回 400,把那句話原樣顯示在該列旁邊,
 * **不預先算好把按鈕灰掉**(慣例同 REQ-20260807001)。
 */
import { useEffect, useState } from 'react';
import {
  FREIGHT_PAYMENT_TYPES,
  SHIPPING_METHODS,
  resolveFreightWorkOrder,
  shipmentReceiptTotal,
  type CreateShipmentInput,
  type FreightPaymentType,
  type OrderShipmentLine,
  type ShippingMethod,
} from '@app/shared';
import { Modal } from '../../components/Modal';
import { getApiErrorMessage } from '../../lib/api-client';
import { formatMoney } from '../../lib/format-money';
import { useCreateShipment, useOrderShipments, useUpdateShipmentDate } from '../accounting/api';
import { useSupplier } from '../suppliers/api';
import { SupplierSearchModal } from '../suppliers/SupplierSearchModal';

export interface ShipmentModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  orderNo: string;
  /**
   * 送出成功後的收尾(選填,REQ-20260826002)。不傳就維持原行為 —— 彈窗留在原地,
   * 可以接著登記下一筆。要不要收掉彈窗 / 導頁由呼叫端自己決定,彈窗不預設立場。
   */
  onShipped?: () => void;
}

export function ShipmentModal({
  open,
  onClose,
  orderId,
  orderNo,
  onShipped,
}: ShipmentModalProps): JSX.Element {
  const query = useOrderShipments(open ? orderId : undefined);
  const mutation = useCreateShipment(orderId);
  /** 勾選的品項 id → 這次要出的數量(字串,維持 decimal 精度) */
  const [picked, setPicked] = useState<Record<number, string>>({});
  const [shippedAt, setShippedAt] = useState(todayInTaipei);
  const [note, setNote] = useState('');
  /** 本次收款(選填)。欄位比照 ReceiptModal,金額同樣以字串保精度 */
  const [cashAmount, setCashAmount] = useState('');
  const [checkAmount, setCheckAmount] = useState('');
  /** 支票兌現日期 / 匯款帳號末 4 碼(REQ-20260817006),都不與金額互相要求 */
  const [checkClearDate, setCheckClearDate] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferAccountLast4, setTransferAccountLast4] = useState('');
  const [usePrepaid, setUsePrepaid] = useState('');
  /**
   * 寄送方式(必填,REQ-20260908002)。**沒有預設值** —— 預設一個「客戶自取」
   * 等於幫使用者做了決定,而這一格的目的正是把它問出來。
   */
  const [shippingMethod, setShippingMethod] = useState<ShippingMethod | null>(null);
  const [shippingMethodNote, setShippingMethodNote] = useState('');
  /**
   * 委外貨運才展開的四格。承運商存的是**廠商 id** —— 貨運公司一律要建檔在廠商
   * 資料裡(REQ-20260817007 的需求原文),所以用既有的廠商搜尋開窗挑,
   * 不做自由文字輸入、也不做 select。
   */
  const [carrierSupplierId, setCarrierSupplierId] = useState<number | null>(null);
  const [trackingNo, setTrackingNo] = useState('');
  const [shippingFee, setShippingFee] = useState('');
  const [carrierPickerOpen, setCarrierPickerOpen] = useState(false);
  /** 這筆運費要不要變成應付。三選一,取代舊的「☑ 運費已付款 + 現金 / 支票」 */
  const [freightPaymentType, setFreightPaymentType] = useState<FreightPaymentType | null>(null);
  /**
   * 正在改日期的那筆出貨紀錄(REQ-20260825003)。一次只開一列 ——
   * 改日期是「發現 KEY 錯了就去修那一筆」,不是批次作業。
   */
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDate, setEditingDate] = useState('');
  const dateMutation = useUpdateShipmentDate(orderId);

  // 換一張單(或關掉再開)時把上一次的勾選清掉 —— 留著會把 A 單的數量帶到 B 單上
  useEffect(() => {
    setPicked({});
    setNote('');
    setCashAmount('');
    setCheckAmount('');
    setCheckClearDate('');
    setTransferAmount('');
    setTransferAccountLast4('');
    setUsePrepaid('');
    setShippingMethod(null);
    setShippingMethodNote('');
    setCarrierSupplierId(null);
    setTrackingNo('');
    setShippingFee('');
    setFreightPaymentType(null);
    setEditingId(null);
  }, [orderId, open]);

  const data = query.data;
  const lines = data?.lines ?? [];
  const selected = lines
    .map((line) => ({ line, qty: picked[line.orderLineId] }))
    .filter((r): r is { line: OrderShipmentLine; qty: string } => r.qty !== undefined);
  const over = selected.filter((r) => Number(r.qty) > Number(r.line.remainingQty));
  const invalid = selected.filter((r) => !(Number(r.qty) > 0));

  // 收款的檢查與出貨的完全分開:超賣是出貨的錯,超收是收款的錯
  const receiptTotal = shipmentReceiptTotal({
    cashAmount,
    checkAmount,
    transferAmount,
    usePrepaid,
  });
  const outstanding = Number(data?.outstanding ?? 0);
  const overpaid = receiptTotal > outstanding;

  // 選到的承運商簡稱。`onPick` 只給 id(不改既有元件的介面),名稱走既有的
  // 單筆廠商查詢 —— react-query 會快取,同一家挑第二次不會再打一次
  const carrierQuery = useSupplier(carrierSupplierId ?? undefined);

  /** 這次會不會留下一筆運費明細(REQ-20260908002)。條件與後端逐字相同 */
  const isCarrier = shippingMethod === 'CARRIER';
  /** 到付由客戶付給貨運行 —— 金額改為選填 */
  const isCollect = freightPaymentType === 'COLLECT';
  /**
   * 委外貨運的必填還沒填齊 —— 與超賣 / 超收是**三種不同的錯**,
   * 訊息與 testid 都各自獨立,不混在一起。
   * 條件逐項對應 `CreateShipmentSchema` 的 superRefine(前端只是不要讓使用者
   * 按下去才發現,真正的守門在 zod)。
   */
  const freightIncomplete =
    isCarrier &&
    (carrierSupplierId == null ||
      freightPaymentType == null ||
      (!isCollect && !(Number(shippingFee) > 0)));
  const otherNoteMissing = shippingMethod === 'OTHER' && !shippingMethodNote.trim();

  /**
   * 這一趟運費會歸到哪張工單(REQ-20260908002 圖一的三種提示)。
   * ⚠️ `shipments.orderId` 是**單一 FK**,所以這裡永遠只有一張 → 綠底那一則。
   * 判斷本身走 shared 的 `resolveFreightWorkOrder()`,與後端寫進 DB 的是同一份規則 ——
   * 兩邊各判一次就會出現「後端存待分攤、畫面說已自動帶入」。
   */
  const freightWorkOrder = resolveFreightWorkOrder([orderNo]);

  const toggle = (line: OrderShipmentLine): void => {
    setPicked((prev) => {
      const next = { ...prev };
      // 勾起來時預帶入未出貨數量,要分批出再自己改小
      if (next[line.orderLineId] === undefined) next[line.orderLineId] = line.remainingQty;
      else delete next[line.orderLineId];
      return next;
    });
  };

  const submit = (): void => {
    const input: CreateShipmentInput = {
      shippedAt,
      note: note.trim() || null,
      lines: selected.map((r) => ({ orderLineId: r.line.orderLineId, qty: r.qty })),
      // 留白就是沒收款,後端看到 0 / null 就只登記出貨
      cashAmount: cashAmount.trim() || null,
      checkAmount: checkAmount.trim() || null,
      checkClearDate: checkClearDate.trim() || null,
      transferAmount: transferAmount.trim() || null,
      transferAccountLast4: transferAccountLast4.trim() || null,
      usePrepaid: usePrepaid.trim() || null,
      addPrepaid: null,
      // 寄送方式必填 —— 送出鈕在沒選之前是停用的,這裡的 fallback 只為型別
      shippingMethod: shippingMethod ?? 'PICKUP',
      shippingMethodNote: shippingMethod === 'OTHER' ? shippingMethodNote.trim() || null : null,
      // 不是委外貨運時四格一律送 null(後端也會再落一次 null,兩邊同一條規則)
      carrierSupplierId: isCarrier ? carrierSupplierId : null,
      trackingNo: isCarrier ? trackingNo.trim() || null : null,
      shippingFee: isCarrier ? shippingFee.trim() || null : null,
      freightPaymentType: isCarrier ? freightPaymentType : null,
    };
    mutation.mutate(input, {
      onSuccess: () => {
        /*
         * 彈窗**預設仍然不自己關**(REQ-20260815008):分批出貨時使用者常常接著
         * 登記下一筆,而且要看到紀錄真的留下來了。所以這裡只把欄位清乾淨。
         *
         * 送出成功後要不要收掉彈窗 / 導去別頁,交給呼叫端的 `onShipped`
         * (REQ-20260826002)—— 入口不同,該去的地方也不同(從儀表板未出貨清單
         * 深連結進來的做完一筆要回首頁看大局;在會計頁自己按「出貨」的留在原地)。
         */
        setPicked({});
        setNote('');
        setCashAmount('');
        setCheckAmount('');
        setCheckClearDate('');
        setTransferAmount('');
        setTransferAccountLast4('');
        setUsePrepaid('');
        setShippingMethod(null);
        setShippingMethodNote('');
        setCarrierSupplierId(null);
        setTrackingNo('');
        setShippingFee('');
        setFreightPaymentType(null);
        // 放在最後:呼叫端可能直接把彈窗收掉,先確保欄位已經清乾淨
        onShipped?.();
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`登記出貨 ${orderNo}`}
      dismissOnBackdrop={false}
      widthClass="max-w-3xl"
    >
      <div className="grid gap-4">
        {query.isLoading ? (
          <div role="status" className="text-slate-500 text-sm">
            載入出貨進度中...
          </div>
        ) : query.isError || !data ? (
          <div role="alert" className="text-red-600 text-sm">
            載入失敗：{getApiErrorMessage(query.error, '未知錯誤')}
          </div>
        ) : (
          <>
            {!data.canShip ? (
              <div
                role="alert"
                className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm"
                data-testid="shipment-cannot-ship"
              >
                {/* REQ-20260929003:草稿直接講下一步(與後端 400 同一個意思),其他狀態沿用通用說法 */}
                {data.orderStatus === 'DRAFT' ? (
                  '這張單還是草稿,請先在出貨管理清單按「過帳」後再登記出貨。'
                ) : (
                  <>
                    只有已過帳的出貨單可以登記出貨。這張單目前是「
                    {ORDER_STATUS_LABELS[data.orderStatus] ?? data.orderStatus}」。
                  </>
                )}
              </div>
            ) : null}

            <table className="w-full bg-white shadow-sm rounded-md">
              <thead>
                <tr className="border-b text-left text-sm text-slate-600">
                  <th className="px-4 py-2 w-10"> </th>
                  <th className="px-4 py-2">品項</th>
                  <th className="px-4 py-2 text-right">訂單數量</th>
                  <th className="px-4 py-2 text-right">已出貨</th>
                  <th className="px-4 py-2 text-right">未出貨</th>
                  <th className="px-4 py-2 text-right">本次出貨</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const qty = picked[line.orderLineId];
                  const done = Number(line.remainingQty) <= 0;
                  return (
                    <tr
                      key={line.orderLineId}
                      className="border-b last:border-0 hover:bg-slate-50 text-sm"
                    >
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          aria-label={`選擇 ${line.productName}`}
                          data-testid={`shipment-pick-${line.orderLineId}`}
                          disabled={done || !data.canShip}
                          checked={qty !== undefined}
                          onChange={() => toggle(line)}
                        />
                      </td>
                      <td className="px-4 py-2">
                        {line.productName}
                        {line.productSpec ? (
                          <span className="text-slate-500 text-xs ml-1">{line.productSpec}</span>
                        ) : null}
                        {done ? (
                          <span className="ml-2 text-xs text-slate-500">已出完</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {line.orderedQty}
                        {line.unitName ? (
                          <span className="text-slate-400 text-xs ml-0.5">{line.unitName}</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">{line.shippedQty}</td>
                      <td
                        className="px-4 py-2 text-right font-mono"
                        data-testid={`shipment-remaining-${line.orderLineId}`}
                      >
                        {line.remainingQty}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`${line.productName} 本次出貨數量`}
                          data-testid={`shipment-qty-${line.orderLineId}`}
                          disabled={qty === undefined}
                          value={qty ?? ''}
                          onChange={(e) =>
                            setPicked((prev) => ({
                              ...prev,
                              [line.orderLineId]: e.target.value,
                            }))
                          }
                          className="border rounded-md px-2 py-1 text-sm w-24 text-right font-mono disabled:bg-slate-100"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="grid gap-1">
                <label className="text-sm text-slate-600" htmlFor="shipment-shipped-at">
                  出貨日期
                </label>
                <input
                  id="shipment-shipped-at"
                  type="date"
                  data-testid="shipment-shipped-at"
                  value={shippedAt}
                  onChange={(e) => setShippedAt(e.target.value)}
                  className="border-2 border-black rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="grid gap-1 md:col-span-2">
                <label className="text-sm text-slate-600" htmlFor="shipment-note">
                  備註(選填)
                </label>
                <input
                  id="shipment-note"
                  type="text"
                  data-testid="shipment-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="例:先出一半,其餘下週"
                  className="border rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>

            <section
              className="border rounded-md px-3 py-3 grid gap-3"
              data-testid="shipment-receipt"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-700">本次收款(選填)</h3>
                <span className="text-sm font-medium text-slate-700">
                  這張單未結{' '}
                  <span
                    data-testid="shipment-outstanding"
                    className="font-mono text-base font-bold text-amber-700"
                  >
                    {formatMoney(data.outstanding)}
                  </span>
                </span>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <MoneyField
                  id="shipment-cash"
                  label="現金"
                  value={cashAmount}
                  onChange={setCashAmount}
                  disabled={!data.canShip}
                />
                <MoneyField
                  id="shipment-check"
                  label="支票"
                  value={checkAmount}
                  onChange={setCheckAmount}
                  disabled={!data.canShip}
                />
                <div className="grid gap-1">
                  <label className="text-sm text-slate-600" htmlFor="shipment-check-clear-date">
                    支票兌現日期
                  </label>
                  <input
                    id="shipment-check-clear-date"
                    type="date"
                    data-testid="shipment-check-clear-date"
                    value={checkClearDate}
                    disabled={!data.canShip}
                    onChange={(e) => setCheckClearDate(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm disabled:bg-slate-100"
                  />
                </div>
                <MoneyField
                  id="shipment-transfer"
                  label="匯款"
                  value={transferAmount}
                  onChange={setTransferAmount}
                  disabled={!data.canShip}
                />
                <div className="grid gap-1">
                  <label className="text-sm text-slate-600" htmlFor="shipment-transfer-last4">
                    帳號末4碼
                  </label>
                  <input
                    id="shipment-transfer-last4"
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    data-testid="shipment-transfer-last4"
                    value={transferAccountLast4}
                    disabled={!data.canShip}
                    onChange={(e) => setTransferAccountLast4(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm font-mono disabled:bg-slate-100"
                  />
                </div>
                <MoneyField
                  id="shipment-use-prepaid"
                  label="動用預收款"
                  value={usePrepaid}
                  onChange={setUsePrepaid}
                  disabled={!data.canShip}
                />
              </div>
              <div className="text-sm text-slate-600">
                本次收款合計{' '}
                <span data-testid="shipment-receipt-total" className="font-mono text-slate-900">
                  {receiptTotal.toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-slate-500">
                留白就只登記出貨。填了金額會在同一次送出一併登記收款,固定沖銷這張單;
                要收其他單據的錢請走「登錄收款」。
              </p>
            </section>

            {/*
              寄送方式(REQ-20260908002 圖一)。**必填、四選一**,取代原本
              「三格永遠都在、永遠選填」的做法 —— 那時是沒有值域可靠所以不敢問
              (REQ-20260817007 §二 否決了拿自由文字的 `shippingMethod` 去比對);
              現在有了自己的值域,問得出來也就該問。
              只有「委外貨運」展開四格,只有「其他」展開一格說明。
            */}
            <section
              className="border rounded-md px-3 py-3 grid gap-3"
              data-testid="shipment-shipping"
            >
              <h3 className="text-sm font-semibold text-slate-700">寄送方式 *</h3>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(SHIPPING_METHODS) as ShippingMethod[]).map((method) => (
                  <button
                    key={method}
                    type="button"
                    data-testid={`shipment-method-${method}`}
                    aria-pressed={shippingMethod === method}
                    disabled={!data.canShip}
                    onClick={() => setShippingMethod(method)}
                    className={`rounded border px-3 py-1.5 text-sm disabled:opacity-50 ${
                      shippingMethod === method
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {SHIPPING_METHODS[method]}
                  </button>
                ))}
              </div>

              {/* 「其他」只多一格自由文字 —— 值域外的寄法(客戶司機來載…)要留得下來 */}
              {shippingMethod === 'OTHER' ? (
                <div className="grid gap-1">
                  <label className="text-sm text-slate-600" htmlFor="shipment-method-note">
                    請說明 *
                  </label>
                  <input
                    id="shipment-method-note"
                    type="text"
                    maxLength={100}
                    data-testid="shipment-method-note"
                    value={shippingMethodNote}
                    disabled={!data.canShip}
                    onChange={(e) => setShippingMethodNote(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm disabled:bg-slate-100"
                  />
                </div>
              ) : null}

              {/* 委外貨運:貨運公司 / 託運單號 / 運費金額(未稅)/ 付款方式 */}
              {isCarrier ? (
                <div className="grid gap-3" data-testid="shipment-carrier-fields">
                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="grid gap-1">
                      <span className="text-sm text-slate-600">貨運公司 *</span>
                      <div className="flex items-center gap-2">
                        {carrierSupplierId == null ? (
                          <span
                            className="text-sm text-slate-500"
                            data-testid="shipment-carrier-empty"
                          >
                            尚未選擇
                          </span>
                        ) : (
                          <span
                            className="text-sm text-slate-900"
                            data-testid="shipment-carrier-name"
                          >
                            {carrierQuery.data?.simpleName ?? `廠商 #${carrierSupplierId}`}
                          </span>
                        )}
                        <button
                          type="button"
                          data-testid="shipment-carrier-pick"
                          disabled={!data.canShip}
                          onClick={() => setCarrierPickerOpen(true)}
                          className="px-2 py-1 text-xs rounded border hover:bg-slate-50 disabled:opacity-50"
                        >
                          {carrierSupplierId == null ? '選擇廠商' : '重選'}
                        </button>
                        {carrierSupplierId != null ? (
                          <button
                            type="button"
                            data-testid="shipment-carrier-clear"
                            onClick={() => setCarrierSupplierId(null)}
                            className="px-2 py-1 text-xs rounded border hover:bg-slate-50"
                          >
                            清除
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <div className="grid gap-1">
                      <label className="text-sm text-slate-600" htmlFor="shipment-tracking-no">
                        託運單號
                      </label>
                      <input
                        id="shipment-tracking-no"
                        type="text"
                        maxLength={50}
                        data-testid="shipment-tracking-no"
                        value={trackingNo}
                        disabled={!data.canShip}
                        onChange={(e) => setTrackingNo(e.target.value)}
                        className="border rounded-md px-3 py-2 text-sm font-mono disabled:bg-slate-100"
                      />
                      <span className="text-[11px] text-slate-400">還沒拿到可以留白,之後補</span>
                    </div>
                    {/* ⚠️ 未稅 —— REQ-20260829003 當時當成含稅總額,那正是它選免稅的前提 */}
                    <MoneyField
                      id="shipment-shipping-fee"
                      label={isCollect ? '運費金額(未稅)' : '運費金額 *(未稅)'}
                      value={shippingFee}
                      onChange={setShippingFee}
                      disabled={!data.canShip}
                      {...(isCollect ? { placeholder: '客戶付,可留白' } : {})}
                    />
                  </div>

                  <div className="grid gap-1">
                    <span className="text-sm text-slate-600">付款方式 *</span>
                    <div className="flex flex-wrap gap-2">
                      {(Object.keys(FREIGHT_PAYMENT_TYPES) as FreightPaymentType[]).map((type) => (
                        <button
                          key={type}
                          type="button"
                          data-testid={`shipment-freight-payment-${type}`}
                          aria-pressed={freightPaymentType === type}
                          disabled={!data.canShip}
                          onClick={() => setFreightPaymentType(type)}
                          className={`rounded border px-3 py-1.5 text-sm disabled:opacity-50 ${
                            freightPaymentType === type
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {FREIGHT_PAYMENT_TYPES[type]}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isCollect ? (
                    <p
                      className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2"
                      data-testid="shipment-freight-collect-hint"
                    >
                      到付不是本公司成本,不列入應付。金額留白也能送出,日後要查再到歸單畫面補。
                    </p>
                  ) : null}

                  {/*
                    歸屬工單的三種情況(圖一)。狀態由 shared 的
                    `resolveFreightWorkOrder()` 算,與後端寫進 DB 的是同一份規則。
                  */}
                  {freightWorkOrder.workOrderState === 'NORMAL' ? (
                    <p
                      className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2"
                      data-testid="shipment-freight-work-order-hint"
                    >
                      歸屬工單 {freightWorkOrder.salesOrderNo} —— 本張出貨單只對應一張工單,
                      已自動帶入。歸單時直接入帳,不需補資料。
                    </p>
                  ) : freightWorkOrder.workOrderState === 'PENDING_ALLOCATION' ? (
                    <p
                      className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2"
                      data-testid="shipment-freight-work-order-hint"
                    >
                      本張出貨單含 {orderNo} 等多張工單,運費分攤方式在歸單時決定,
                      預設按各品項金額比例分攤。出貨端不需處理。
                    </p>
                  ) : (
                    <p
                      className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2"
                      data-testid="shipment-freight-work-order-hint"
                    >
                      本張出貨單沒有關聯工單。成本去向在歸單時指定,通常選「計入營業費用」。
                      出貨端不需處理。
                    </p>
                  )}

                  <p className="text-xs text-slate-500" data-testid="shipment-freight-hint">
                    送出後<strong>不會</strong>直接建立進貨單,只留下一筆<strong>運費明細</strong>;
                    月底到「運費歸單」把同一家貨運行的明細彙整成一張進貨單、
                    核對過對帳單再入帳。
                  </p>
                </div>
              ) : null}

              {shippingMethod == null ? (
                <p className="text-xs text-slate-500">
                  必填。自取 / 自送只記錄值,不展開任何欄位;委外貨運才需要填貨運公司與運費。
                </p>
              ) : null}
            </section>

            <SupplierSearchModal
              open={carrierPickerOpen}
              onClose={() => setCarrierPickerOpen(false)}
              defaultCategoryName="郵寄運送"
              onPick={(id) => {
                setCarrierSupplierId(id);
                setCarrierPickerOpen(false);
              }}
            />

            {over.length > 0 ? (
              <div role="alert" className="text-red-600 text-sm" data-testid="shipment-over-alert">
                {over.map((r) => r.line.productName).join('、')} 的出貨數量超過未出貨數量。
              </div>
            ) : null}
            {overpaid ? (
              <div
                role="alert"
                className="text-red-600 text-sm"
                data-testid="shipment-receipt-over-alert"
              >
                本次收款 {receiptTotal.toFixed(2)} 超過這張單的未結餘額 {data.outstanding}。
              </div>
            ) : null}
            {/*
              委外貨運的必填還沒填齊(REQ-20260908002)。
              **與超賣、超收是三種不同的錯**,testid 與訊息都不共用。
            */}
            {freightIncomplete ? (
              <div
                role="alert"
                className="text-red-600 text-sm"
                data-testid="shipment-freight-incomplete-alert"
              >
                委外貨運請選擇貨運公司與付款方式,並填寫運費金額(到付可留白)。
              </div>
            ) : null}
            {otherNoteMissing ? (
              <div
                role="alert"
                className="text-red-600 text-sm"
                data-testid="shipment-method-note-alert"
              >
                寄送方式選「其他」時請說明。
              </div>
            ) : null}
            {mutation.isError ? (
              <div role="alert" className="text-red-600 text-sm">
                登記失敗：{getApiErrorMessage(mutation.error, '未知錯誤')}
              </div>
            ) : null}

            <div className="flex items-center justify-between border-t pt-3">
              <div className="text-sm text-slate-600">
                已選 {selected.length} 項,本次出貨合計{' '}
                <span data-testid="shipment-total" className="font-mono text-slate-900">
                  {sumQty(selected.map((r) => r.qty))}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 rounded border hover:bg-slate-50"
                >
                  關閉
                </button>
                <button
                  type="button"
                  data-testid="shipment-submit"
                  onClick={submit}
                  disabled={
                    !data.canShip ||
                    selected.length === 0 ||
                    over.length > 0 ||
                    invalid.length > 0 ||
                    overpaid ||
                    shippingMethod == null ||
                    freightIncomplete ||
                    otherNoteMissing ||
                    mutation.isPending
                  }
                  className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {mutation.isPending ? '登記中...' : '確認出貨'}
                </button>
              </div>
            </div>

            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-1">歷次出貨紀錄</h3>
              {data.shipments.length === 0 ? (
                <p className="text-slate-500 text-sm">這張單還沒有任何出貨紀錄。</p>
              ) : (
                <ul className="grid gap-1 text-sm" data-testid="shipment-history">
                  {data.shipments.map((s) => (
                    <li key={s.id} className="border rounded px-3 py-1.5">
                      <div className="flex flex-wrap items-baseline gap-2">
                        {editingId === s.id ? (
                          <>
                            <input
                              type="date"
                              aria-label="出貨日期"
                              data-testid={`shipment-edit-date-input-${s.id}`}
                              value={editingDate}
                              onChange={(e) => setEditingDate(e.target.value)}
                              className="border rounded px-2 py-0.5 text-sm"
                            />
                            <button
                              type="button"
                              data-testid={`shipment-edit-date-save-${s.id}`}
                              disabled={!editingDate || dateMutation.isPending}
                              onClick={() =>
                                dateMutation.mutate(
                                  { shipmentId: s.id, shippedAt: editingDate },
                                  // 成功就收回純顯示;失敗時**留在編輯狀態**,
                                  // 使用者才看得到錯誤訊息、也不用重打一次日期
                                  { onSuccess: () => setEditingId(null) },
                                )
                              }
                              className="px-2 py-0.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              {dateMutation.isPending ? '儲存中...' : '儲存'}
                            </button>
                            <button
                              type="button"
                              data-testid={`shipment-edit-date-cancel-${s.id}`}
                              onClick={() => setEditingId(null)}
                              className="px-2 py-0.5 text-xs rounded border hover:bg-slate-50"
                            >
                              取消
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="font-mono">{s.shippedAt.slice(0, 10)}</span>
                            <button
                              type="button"
                              data-testid={`shipment-edit-date-${s.id}`}
                              onClick={() => {
                                setEditingId(s.id);
                                setEditingDate(s.shippedAt.slice(0, 10));
                                // 上一列改失敗留下的紅字不要跟著跑到這一列來
                                dateMutation.reset();
                              }}
                              className="px-2 py-0.5 text-xs rounded border hover:bg-slate-50"
                            >
                              修改日期
                            </button>
                          </>
                        )}
                        <span className="text-slate-600">
                          {s.lines.map((l) => `${l.productName} × ${l.qty}`).join('、')}
                        </span>
                        <span className="text-slate-400 text-xs ml-auto">
                          {s.shippedByName ?? '—'}
                        </span>
                      </div>
                      {/*
                        已沖帳的單後端回 400「這張出貨單已有沖帳紀錄,不可修改出貨日期」——
                        原話就是使用者要的下一步指示,顯示在出錯的那一列旁邊
                      */}
                      {editingId === s.id && dateMutation.isError ? (
                        <div
                          role="alert"
                          className="text-red-600 text-xs"
                          data-testid={`shipment-edit-date-error-${s.id}`}
                        >
                          {getApiErrorMessage(dateMutation.error, '未知錯誤')}
                        </div>
                      ) : null}
                      {/* 這筆是寄送的話,看得到由誰寄、單號與運費(REQ-20260817007) */}
                      {s.shippingMethod ||
                      s.carrierName ||
                      s.trackingNo ||
                      s.shippingFee ||
                      s.freightPurchaseOrderNo ? (
                        <div
                          className="text-slate-500 text-xs"
                          data-testid={`shipment-history-shipping-${s.id}`}
                        >
                          {[
                            // 寄送方式(REQ-20260908002)。舊紀錄沒有這一格,不回填
                            s.shippingMethod
                              ? (SHIPPING_METHODS[s.shippingMethod as ShippingMethod] ??
                                s.shippingMethod)
                              : null,
                            s.shippingMethodNote,
                            s.carrierName,
                            s.trackingNo ? `單號 ${s.trackingNo}` : null,
                            s.shippingFee ? `運費 ${s.shippingFee}` : null,
                            // 這筆的運費明細歸單了沒(REQ-20260908002)
                            s.freightLine
                              ? s.freightLine.status === 'CONSOLIDATED'
                                ? '運費已歸單入帳'
                                : '運費待歸單'
                              : null,
                            // 舊流程自動入帳的那張進貨單(REQ-20260829003)。
                            // **保留顯示** —— 那是歷史事實,不再有新的值但仍要查得到
                            s.freightPurchaseOrderNo
                              ? `運費進貨單 ${s.freightPurchaseOrderNo}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' ・ ')}
                        </div>
                      ) : null}
                      {/*
                        出貨單異動的提示(REQ-20260908002 圖一最後那張表)。
                        出貨紀錄是 append-only(REQ-20260817007 §二),**沒有作廢入口**,
                        目前唯一改得動的是出貨日期 —— 已歸單時那筆運費不會跟著改,
                        所以要講清楚。
                      */}
                      {s.freightLine?.status === 'CONSOLIDATED' ? (
                        <div
                          className="text-amber-700 text-xs"
                          data-testid={`shipment-history-freight-locked-${s.id}`}
                        >
                          本單有已入帳的運費 {s.freightLine.amount ?? '—'} 元,
                          異動後請確認成本歸屬是否仍正確。
                        </div>
                      ) : null}
                      {s.note ? <div className="text-slate-500 text-xs">{s.note}</div> : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}

/** 收款金額輸入框。樣式與互動比照 ReceiptModal 的同名元件(那支是它的私有元件) */
function MoneyField({
  id,
  label,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  /** 到付時的「客戶付,可留白」。不傳時 DOM 與加這個 prop 之前逐字相同 */
  placeholder?: string;
}): JSX.Element {
  return (
    <div className="grid gap-1">
      <label className="text-sm text-slate-600" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        data-testid={id}
        value={value}
        disabled={disabled}
        {...(placeholder != null ? { placeholder } : {})}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded-md px-3 py-2 text-sm text-right font-mono disabled:bg-slate-100"
      />
    </div>
  );
}

/** 與出貨管理列表同一份對照,狀態字串只在提示句裡用得到 */
const ORDER_STATUS_LABELS: Record<string, string> = {
  DRAFT: '草稿',
  POSTED: '已過帳',
  VOIDED: '已作廢',
  HISTORICAL: '歷史單',
};

/** 顯示用的合計。權威數量由後端以 Decimal 重算,這裡只是給人看的即時回饋。 */
function sumQty(values: string[]): string {
  const total = values.reduce((sum, v) => sum + (Number(v) || 0), 0);
  return String(Math.round(total * 10000) / 10000);
}

/** 出貨日期預設今天(台北)—— 容器與瀏覽器時區都不一定是台北,明確指定 */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
